<template>
  <aside v-if="isVisible" class="deployment-info" data-testid="deployment-info" aria-label="Deployment information">
    <button
      type="button"
      class="deployment-info__close"
      aria-label="Hide deployment information"
      data-testid="deployment-info-close"
      @click="isDismissed = true"
    />
    <div class="deployment-info__block">
      <p>Frontend: {{ frontendBranchName }}</p>
      <p>Deployed: {{ frontendDeploymentTime }}</p>
    </div>
    <div class="deployment-info__block">
      <p>Backend: {{ backendBranchName || '-' }}</p>
      <p>Deployed: {{ backendDeploymentTime || '-' }}</p>
    </div>
  </aside>
</template>

<script setup lang="ts">
interface DeploymentResponse {
  branchName?: string
  deploymentTime?: string
}

const runtimeConfig = useRuntimeConfig()
const isDismissed = ref(false)

const toNonEmptyString = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

const frontendBranchName = computed(() => toNonEmptyString(runtimeConfig.public.OTA_BRANCH_NAME))
const frontendDeploymentTime = computed(() => toNonEmptyString(runtimeConfig.public.OTA_DEPLOYMENT_TIME))
const shouldRender = computed(() => frontendBranchName.value !== '' && frontendDeploymentTime.value !== '')
const isVisible = computed(() => shouldRender.value && !isDismissed.value)

const normalizeBackendInfo = (response: DeploymentResponse) => {
  return {
    branchName: toNonEmptyString(response.branchName),
    deploymentTime: toNonEmptyString(response.deploymentTime)
  }
}

let backendResponse: DeploymentResponse = {}

const { data, error } = useFetch<DeploymentResponse>(`${runtimeConfig.public.API_URL}/v2/deployment`, {
  server: true,
  immediate: shouldRender.value,
  default: () => ({})
})
backendResponse = error.value ? {} : data.value ?? {}

const { branchName: backendBranchName, deploymentTime: backendDeploymentTime } = normalizeBackendInfo(backendResponse)
</script>

<style scoped>
.deployment-info {
  position: fixed;
  left: 1rem;
  bottom: 1rem;
  z-index: 20;
  padding: 1rem;
  border: 1px solid #787878;
  border-radius: 0.375rem;
  background: rgba(255, 255, 255, 0.6 );
  color: #5c5c5e;
  font-size: 1rem;
  line-height: 1.5;
  pointer-events: auto;
}

.deployment-info__close {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 1.5rem;
  height: 1.5rem;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.deployment-info__close::before,
.deployment-info__close::after {
  content: '';
  position: absolute;
  top: 0.7rem;
  left: 0.2rem;
  width: 1.1rem;
  height: 1px;
  background-color: #787878;
}

.deployment-info__close::before {
  transform: rotate(45deg);
}

.deployment-info__close::after {
  transform: rotate(-45deg);
}

.deployment-info__close:hover::before,
.deployment-info__close:hover::after {
  background-color: #C8C8C8;
}

.deployment-info__block + .deployment-info__block {
  margin-top: 0.4rem;
}
</style>
